// Tooltip.tsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface TooltipProps {
  message: string;
  width?: string; // optional width like "w-64", "w-48"
  width_height_icon?: string;
  direction?: 'top' | 'bottom';
}

const Tooltip: React.FC<TooltipProps> = ({ message, width = 'w-64', width_height_icon, direction = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const calculatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: direction === 'top' ? rect.top : rect.bottom,
        left: rect.left + rect.width / 2,
      });
    }
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
    }
    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible, direction]);

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        className={"flex text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-200 " +
          (width_height_icon ? width_height_icon : "w-3 h-3")
        }
        onMouseEnter={() => {
          calculatePosition();
          setIsVisible(true);
        }}
        onMouseLeave={() => setIsVisible(false)}
        onClick={(e) => {
          e.stopPropagation(); // Prevent event bubbling
          if (!isVisible) calculatePosition();
          setIsVisible(!isVisible);
        }}
      >
        <Info className={width_height_icon ? width_height_icon : "w-3 h-3"} strokeWidth={2} />
      </div>

      {isVisible && createPortal(
        <div
          className="fixed left-0 top-0 pointer-events-none"
          style={{ zIndex: 9999 }}
        >
          <div
            className={`absolute px-4 flex justify-center ${width}`}
            style={{
              top: coords.top,
              left: coords.left,
              transform: 'translateX(-50%) ' + (direction === 'top' ? 'translateY(-100%)' : 'translateY(0)'),
            }}
          >
            <div
              className={`bg-blue-50 font-normal text-sm px-4 py-2 rounded-lg shadow-xl-0-19-40-8 border border-gray-300 relative pointer-events-none`}
            >
              <div
                className="bg-blue-50 font-normal text-sm rounded-lg"
                dangerouslySetInnerHTML={{
                  __html: message,
                }}
              ></div>

              {/* Triangle */}
              <div className={`absolute left-1/2 transform -translate-x-1/2 w-[19px] h-[14px] z-40 ${direction === 'top'
                ? 'top-full -mt-[1px]'
                : 'bottom-full -mb-[1px] rotate-180'
                }`}>
                <svg
                  width="19"
                  height="14"
                  viewBox="0 0 19 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.9754 12.2043C8.77413 13.1451 10.2259 13.1451 11.0246 12.2043L17.5282 4.54445C18.6313 3.24526 17.7079 1.25 16.0036 1.25H2.99641C1.29211 1.25 0.36875 3.24526 1.47182 4.54445L7.9754 12.2043Z"
                    fill="#eff6ff"
                  />
                  <path
                    d="M7.59473 12.5283C8.59308 13.7036 10.4069 13.7036 11.4053 12.5283L17.9092 4.86816C19.2879 3.24426 18.1341 0.75025 16.0039 0.75H2.99609C0.865921 0.75025 -0.287945 3.24426 1.09082 4.86816L7.59473 12.5283Z"
                    stroke="#3C82F6"
                    strokeOpacity="0.25"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
